/**
 * Carga del script de Cloudflare Turnstile, una sola vez por página, con salida si no llega.
 *
 * Antes la promesa no tenía `onerror` ni timeout: con challenges.cloudflare.com bloqueado (un
 * bloqueador, un DNS filtrado, una red de empresa) quedaba pendiente para siempre, el widget nunca
 * aparecía y el formulario terminaba rebotando sin explicar por qué. Ahora falla con un motivo
 * (`script_error` o `script_timeout`) que llega al panel, y el próximo intento vuelve a probar.
 */

export const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/** Más que esto bajando un script de 50 KB es una red que no lo va a traer. */
export const TURNSTILE_SCRIPT_TIMEOUT_MS = 10_000;

export type TurnstileRenderOptions = Record<string, unknown>;

export type TurnstileApi = {
  render: (el: HTMLElement, opts: TurnstileRenderOptions) => string | undefined;
  remove: (widgetId: string) => void;
  reset: (widgetId?: string) => void;
  execute?: (container: string | HTMLElement, opts?: TurnstileRenderOptions) => void;
  getResponse?: (widgetId?: string) => string | undefined;
  isExpired?: (widgetId?: string) => boolean;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export type TurnstileScriptFailure = "script_error" | "script_timeout";

export class TurnstileScriptError extends Error {
  constructor(readonly reason: TurnstileScriptFailure) {
    super(reason);
    this.name = "TurnstileScriptError";
  }
}

let loading: Promise<TurnstileApi> | null = null;

function findScript(): HTMLScriptElement | null {
  return document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
}

export function ensureTurnstileScript(): Promise<TurnstileApi> {
  if (typeof window === "undefined") {
    return Promise.reject(new TurnstileScriptError("script_error"));
  }
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loading) return loading;

  loading = new Promise<TurnstileApi>((resolve, reject) => {
    let script = findScript();
    if (!script) {
      script = document.createElement("script");
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const settle = (outcome: TurnstileApi | TurnstileScriptFailure) => {
      clearTimeout(timer);
      script?.removeEventListener("load", onLoad);
      script?.removeEventListener("error", onError);
      if (typeof outcome !== "string") return resolve(outcome);
      // El script que falló se saca: así el próximo intento lo vuelve a pedir en vez de
      // quedarse esperando un `load` que ya no va a llegar.
      script?.remove();
      loading = null;
      reject(new TurnstileScriptError(outcome));
    };
    const onLoad = () => settle(window.turnstile ?? "script_error");
    const onError = () => settle("script_error");
    const timer = setTimeout(
      () => settle(window.turnstile ?? "script_timeout"),
      TURNSTILE_SCRIPT_TIMEOUT_MS,
    );

    script.addEventListener("load", onLoad);
    script.addEventListener("error", onError);
  });

  return loading;
}

/** Sólo para tests. */
export function __resetTurnstileScriptForTests() {
  loading = null;
}
